import EyeClose from 'assets/icons/EyeClose';
import EyeShow from 'assets/icons/EyeShow';
import BaseButton from 'components/ui/BaseButton';
import EmailInput from 'components/common/inputs/EmailInput';
import { AUTH_ACTION_TYPE, PASSWORD_INPUT_MODE } from 'enums';
import { AuthPageContext } from 'dashboard/auth/AuthActionsWrapper';
import { t } from 'services/i18n.service';
import { useCallback, useContext, useMemo, useState } from 'react';
import PasswordConditions from './PasswordConditions';
import { ATTRIB } from 'constants/test';

interface PasswordInputProps {
  mode?: PASSWORD_INPUT_MODE;
  placeholder?: string;
  hasAttemptedSubmit?: boolean;
  isValidPassword?: boolean;
}

const testIds = {
  register: ATTRIB.TEST.INPUT_PASSWORD_SIGN_UP,
  reset: ATTRIB.TEST.INPUT_EMAIL_RESET_PASSWORD
};

const PasswordInput = ({
  mode,
  placeholder,
  hasAttemptedSubmit = false,
  isValidPassword = false
}: PasswordInputProps) => {
  const {
    state: { newPassword, password, isProcessing, googleLoginInProgress },
    dispatch
  } = useContext(AuthPageContext);
  const [showPassword, setShowPassword] = useState(false);

  const [registerMode, resetMode] = useMemo(
    () => [mode === PASSWORD_INPUT_MODE.REGISTER, mode === PASSWORD_INPUT_MODE.RESET],
    [mode]
  );

  const value = useMemo(() => {
    if (registerMode || resetMode) {
      return newPassword;
    } else {
      return password;
    }
  }, [registerMode, password, newPassword, resetMode]);

  const handleOnChange = useCallback(
    (value: string) => {
      let type = AUTH_ACTION_TYPE.UPDATE_PASSWORD;
      if (registerMode || resetMode) {
        type = AUTH_ACTION_TYPE.UPDATE_NEW_PASSWORD;
      }
      dispatch?.({
        type,
        value
      });
    },
    [registerMode, dispatch, resetMode]
  );

  const shouldDisableActions = useMemo(
    () => isProcessing || googleLoginInProgress,
    [isProcessing, googleLoginInProgress]
  );

  const showPasswordError = useMemo(
    () => hasAttemptedSubmit && (registerMode || resetMode) && !isValidPassword,
    [hasAttemptedSubmit, registerMode, resetMode, isValidPassword]
  );

  return (
    <div className='flex flex-col gap-2'>
      <div className='relative'>
        <EmailInput
          type={showPassword ? 'text' : 'password'}
          placeholder={placeholder ?? t('auth_page.password')}
          value={value}
          onChange={({ target: { value } }) => {
            handleOnChange(value);
          }}
          readOnly={shouldDisableActions}
          label={t('auth_page.password')}
          hasError={showPasswordError}
          data-testid={
            mode ? testIds[mode] : ATTRIB.TEST.INPUT_PASSWORD_SIGN_IN
          }
        />
        <BaseButton
          icon={showPassword ? <EyeShow /> : <EyeClose />}
          additionalStyles='absolute top-1/2 -translate-y-1/2 right-0 hover:scale-100'
          onClick={() => setShowPassword((prev) => !prev)}
          variant='icon'
        />
      </div>
      {registerMode || resetMode ? (
        <PasswordConditions showTitle={hasAttemptedSubmit} />
      ) : null}
      {!mode ? (
        <BaseButton
          type='button'
          title={t('auth_page.forgot_password')}
          onClick={() =>
            dispatch?.({ type: AUTH_ACTION_TYPE.NAVIGATE_TO_FORGOT_PASSWORD })
          }
          variant='link'
          additionalStyles='w-fit'
          size='sm'
          disabled={shouldDisableActions}
        />
      ) : null}
    </div>
  );
};

export default PasswordInput;
